import {liteClient as algoliasearch} from 'algoliasearch/lite';
import {createAutocomplete} from '@algolia/autocomplete-core';
import {getAlgoliaResults, getAlgoliaFacets} from '@algolia/autocomplete-preset-algolia';

import aa from 'search-insights';
import prestashop from 'prestashop';

export default class AlgoliaAutocompleteCore {
  constructor() {
    // Initialize Algolia client
    this.searchClient = algoliasearch(process.env.ALGOLIA_APP_ID, process.env.ALGOLIA_API_KEY);

    // Animation state tracking
    this.isExpanded = false;
    this.isAnimating = false;

    // Create a debounced function for search
    this.debounced = this.debouncePromise(
      (items) => Promise.resolve(items),
      300, // Reduced debounce time for better responsiveness
    );

    const placeholders = [
      'Search 50,000 Components...',
      'Silicone Tapered Plugs...',
      'M6 x 95MM Countersunk Screws...',
      '50mm Soft Wheel Castors...',
    ];

    this.cyclePlaceholders(document.getElementById('search-input'), placeholders);
    this.initAutocomplete();
  }

  cyclePlaceholders(element, texts, delay = 200, hold = 10000) {
    let textIndex = 0;
    let charIndex = 0;
    let isFocused = false;
    let typingInterval;
    let clearingInterval;

    function typing() {
      if (charIndex < texts[textIndex].length) {
        if (!isFocused) {
          element.placeholder += texts[textIndex].charAt(charIndex);
          charIndex++;
        }
        typingInterval = setTimeout(typing, delay);
      } else {
        setTimeout(clearPlaceholder, hold);
      }
    }

    function startTyping() {
      if (typingInterval) clearInterval(typingInterval);
      if (clearingInterval) clearInterval(clearingInterval);
      charIndex = 0;
      typing();
    }

    function clearPlaceholder() {
      if (charIndex > 0) {
        if (!isFocused) {
          element.placeholder = element.placeholder.slice(0, -1);
          charIndex--;
        }
        clearingInterval = setTimeout(clearPlaceholder, delay);
      } else {
        textIndex = (textIndex + 1) % texts.length;
        startTyping();
      }
    }

    element.addEventListener('focus', () => {
      isFocused = true;
      element.placeholder = '';
      if (typingInterval) clearInterval(typingInterval);
      if (clearingInterval) clearInterval(clearingInterval);
    });

    element.addEventListener('blur', () => {
      isFocused = false;
      startTyping();
    });

    setTimeout(startTyping, 1000);
  }

  debouncePromise(fn, time) {
    let timerId;

    return function debounced(...args) {
      if (timerId) {
        clearTimeout(timerId);
      }
      return new Promise((resolve) => {
        timerId = setTimeout(() => resolve(fn(...args)), time);
      });
    };
  }

  /**
         * Smooth expansion animation with proper state management
         */
  expandSearch() {
    if (this.isExpanded || this.isAnimating) return;

    this.isAnimating = true;
    this.isExpanded = true;

    // Add CSS class for smooth transitions
    $('body').addClass('search-expanding');

    if (window.innerWidth >= 1200) {
      $('.header-nav:nth-of-type(1)')
        .addClass('header-expanded-left')
        .removeClass('col-xl-4');
      $('.header-nav:nth-of-type(2)')
        .addClass('header-expanded-center')
        .removeClass('col-xl-4');
      $('.header-nav:nth-of-type(3)')
        .addClass('header-expanded-right')
        .removeClass('col-xl-4');
    }

    // Animate the search results container
    this.animateResultsContainer(() => {
      this.isAnimating = false;
    });
  }

  /**
         * Smooth collapse animation
         */
  collapseSearch() {
    if (!this.isExpanded || this.isAnimating) return;

    this.isAnimating = true;
    this.isExpanded = false;

    $('#search-results').addClass('d-none');
    $('body').removeClass('search-expanding');

    if (window.innerWidth >= 1200) {
      $('.header-nav:nth-of-type(1)')
        .removeClass('header-expanded-left')
        .addClass('col-xl-4');
      $('.header-nav:nth-of-type(2)')
        .removeClass('header-expanded-center')
        .addClass('col-xl-4');
      $('.header-nav:nth-of-type(3)')
        .removeClass('header-expanded-right')
        .addClass('col-xl-4');
    }

    // Small delay to allow header animation to complete
    setTimeout(() => {
      this.isAnimating = false;
    }, 300);
  }

  /**
         * Improved results container animation with proper width matching
         */
  animateResultsContainer(callback) {
    const $resultsContainer = $('#search-results');
    const $panel = $('.aa-Panel');
    // Show container immediately with proper positioning
    $resultsContainer.removeClass('d-none').css({
      opacity: 0,
      // transform: 'translateY(-10px)',
    });

    // Wait for header animation to complete, then get accurate width
    setTimeout(() => {
      // Set panel width to match the expanded search area
      $panel.css({
        maxWidth: '95vw',
      });

      $resultsContainer.animate({
        opacity: 1,
      }, 200);

      if (callback) callback();
    }, this.isExpanded ? 50 : 350);
  }

  /**
         * Initialize the autocomplete functionality
         */
  initAutocomplete() {
    const $container = $('#autocomplete-container');
    const $inputElement = $('#search-input');
    const $panelContainer = $('#search-results .aa-PanelLayout');

    if (!$container.length || !$inputElement.length || !$panelContainer.length) {
      return;
    }

    const autocomplete = createAutocomplete({
      container: $container[0],
      initialState: {
        query: $inputElement.val(),
      },
      openOnFocus: true,
      onStateChange: ({prevState, state}) => {
        if (prevState.query !== state.query) {
          $inputElement.val(state.query);
        }

        $panelContainer.empty();

        if (state.isOpen && state.collections.every((collection) => collection.items.length === 0)) {
          this.renderNoResults($panelContainer);
          return;
        }

        if (state.isOpen && state.collections.some((collection) => collection.items.length > 0)) {
          this.renderResults(state, $panelContainer);
        }
      },
      getSources: ({query, setContext}) => [
        {
          sourceId: 'categories',
          getItems: () => getAlgoliaResults({
            searchClient: this.searchClient,
            queries: [
              {
                clickAnalytics: true,
                indexName: 'prestashop_category',
                query,
                params: {
                  ruleContexts: ['enable-redirect-url'],
                  hitsPerPage: 5,
                },
              },
            ],
          }),
        },
        {
          sourceId: 'products',
          getItems: () => getAlgoliaResults({
            searchClient: this.searchClient,
            queries: [
              {
                clickAnalytics: true,
                indexName: 'prestashop',
                query,
                params: {
                  ruleContexts: ['enable-redirect-url'],
                  hitsPerPage: 10,
                },
              },
            ],
            transformResponse: ({hits, results}) => {
              window.queryID = results[0].queryID;

              setContext({
                nbProducts: results[0].nbHits,
              });

              return hits;
            },
          }),
        },
      ],
    });

    this.autocomplete = autocomplete;

    // Improved event handlers
    $inputElement.on('focus input', () => {
      this.expandSearch();
      autocomplete.setIsOpen(true);
      this.triggerSearch($inputElement.val(), autocomplete);
    });

    $inputElement.on('input', function () {
      const query = $(this).val();
      autocomplete.setQuery(query);
      autocomplete.refresh();
      if (query) {
        autocomplete.setIsOpen(true);
      }
    });

    // Improved outside click handling
    $(document).on('click', (event) => {
      const $target = $(event.target);
      const isInsideSearch = $container.is($target) || $container.has($target).length > 0;
      const isInsideResults = $('#search-results').is($target) || $('#search-results').has($target).length > 0;

      if (!isInsideSearch && !isInsideResults) {
        this.collapseSearch();
        autocomplete.setIsOpen(false);
      }
    });

    // Handle escape key
    $inputElement.on('keydown', (event) => {
      if (event.key === 'Escape') {
        this.collapseSearch();
        autocomplete.setIsOpen(false);
        $inputElement.blur();
      }
    });

    // Handle form submission
    $container.find('form').on('submit', (e) => {
      e.preventDefault();

      let query = $inputElement.val().trim();
      let encode = encodeURIComponent(query);

      if (query.length > 0) {
        window.location.href = 'search?q=' + encode;
      }
    });
  }

  /**
         * Render no results state
         */
  renderNoResults($panelContainer) {
    const $noResults = $(`
      <section class="aa-Source" data-autocomplete-source-id="products">
        <div class="aa-SourceHeader"></div>
        <div class="aa-SourceNoResults">
          <div class="text-center py-4">
            <div>
              <div><img class="img-fluid mb-3" src="${prestashop.shop.logo}" alt="Vital Parts Logo"></div>
              <div>
                <p class="mb-2">Sorry, we couldn't find any results</p>
                <p class="text-muted">Contact us for assistance</p>
              </div>
              <div class="d-flex justify-content-center gap-2 mt-3">
                <button class="btn btn-primary btn-sm btn-livechat">
                  Live Chat <i class="bi bi-person-workspace ms-1"></i>
                </button>
                <a href="tel:01233713581" class="btn btn-primary btn-sm">
                  Call Now <i class="bi bi-telephone-fill ms-1"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
        <div class="aa-SourceFooter"></div>
      </section>
    `);

    $panelContainer.append($noResults);
    $panelContainer.removeClass('d-none');
  }

  /**
         * Render search results
         */
  renderResults(state, $panelContainer) {
    const $panelSections = $('<div class="aa-PanelSections"></div>');
    const $topSection = $('<div class="aa-PanelSection aa-PanelSection--top"></div>');
    const $bottomSection = $('<div class="aa-PanelSection aa-PanelSection--bottom"></div>');

    state.collections.forEach((collection) => {
      const {source, items} = collection;

      if (items.length === 0) return;

      if (source.sourceId === 'categories') {
        const $categoriesSource = this.renderCategoriesSource(items, state.query);
        $topSection.append($categoriesSource);
      }

      if (source.sourceId === 'products') {
        const $productsSource = this.renderProductsSource(items, state.query, state.context?.nbProducts);
        $bottomSection.append($productsSource);
      }
    });

    $panelSections.append($topSection);
    $panelSections.append($bottomSection);
    $panelContainer.append($panelSections);
    $panelContainer.removeClass('d-none');
  }

  triggerSearch(query, autocomplete) {
    autocomplete.setQuery(query);
    autocomplete.refresh();
    if (query) {
      autocomplete.setIsOpen(true);
    }
  }

  renderCategoriesSource(items, query) {
    const template = `
      <section class="aa-Source" data-autocomplete-source-id="categories">
        <div class="aa-SourceHeader">
          <span class="aa-SourceHeaderTitle">Categories</span>
          <div class="aa-SourceHeaderLine"></div>
        </div>
        <ul class="aa-List" role="listbox">
          ${items.map((item, index) => {
      const highlightedName = this.highlightText(item.name || 'Category', query);

      return `
              <li class="aa-Item" id="autocomplete-0-categories-item-${index}" role="option">
                <a href="${item.url}" class="search-item">
                 <div class="aa-ItemIcon aa-ItemIcon--picture aa-ItemIcon--alignTop search-image me-2">
                    <img src="${item.image_url || ''}" alt="${item.name || 'Category'}" width="80" height="80">
                  </div>
                   <div class="d-flex flex-column flex-grow-1 pe-3">
                      <div class="search-title">${highlightedName}</div>
                    <div>
                      ${item.parent_categories && item.parent_categories.length
          ? `<small class="text-muted  my-1">
                              ${item.parent_categories
            .sort((a, b) => a.level_depth - b.level_depth)
            .map((cat) => cat.name)
            .join(' › ')}
                            </small>`
          : ''
        }
                      </div>
                      </div>
                  <i class="bi bi-arrow-right ms-auto me-1"></i>
                </a>
              </li>
            `;
    }).join('')}
        </ul>
      </section>
    `;

    return $(template);
  }

  renderProductsSource(items, query, totalCount) {
    const template = `
      <section class="aa-Source" data-autocomplete-source-id="products">
        <div class="aa-SourceHeader">
          <span class="aa-SourceHeaderTitle">${totalCount || items.length} Products matching "${query}"</span>
          <div class="aa-SourceHeaderLine"></div>
          <a class="aa-ViewAll text-decoration-none" href="search?q=${query}">
            <div class="algolia-search-results text-decoration-none">
              View Results
              <i class="bi bi-box-arrow-in-right ms-2"></i>
            </div>
          </a>
        </div>
        <ul class="aa-List" role="listbox">
          ${items.map((item, index) => {
      const highlightedName = this.highlightText(item.name || 'Product', query);
      const highlightedCode = this.highlightText(item.reference || 'Product', query);

      return `
              <li class="aa-Item" id="autocomplete-0-products-item-${index + 5}" role="option">
                <a href="${item.url || '#'}" class="search-item align-items-stretch" data-product-id="${item.objectID}">
                  <div class="aa-ItemIcon aa-ItemIcon--picture aa-ItemIcon--alignTop search-image me-2">
                    <img src="${item.small_image_url || ''}" alt="${item.name || 'Product'}" width="80" height="80">
                  </div>
                  <div class="d-flex flex-column flex-md-row flex-grow-1 pe-3">
                    <div class="search-description d-flex flex-column justify-content-center">
                      <div class="search-title">${highlightedName}</div>
                      ${item.category ? `
                      <div class="search-view-all-sizes">
                        <a href="/sub-category-${item.category}-p.asp">View All Sizes</a>
                      </div>
                      ` : ''}
                    </div>
                    <div class="search-price d-flex flex-row flex-md-column flex-grow-1 justify-content-between">
                      <div class="search-code">${highlightedCode}</div>
                      <div class="search-price-range">${item.bulk_price || ''}</div>
                    </div>
                  </div>
                </a>
              </li>
            `;
    }).join('')}
        </ul>
        <div class="aa-SourceFooter">
          <div class="text-center py-2">
            <div>
              <div>
                <img class="img-fluid mb-2" src="${prestashop.shop.logo}" alt="Vital Parts Logo">
              </div>
              <div>
                <p>${totalCount || items.length} Products matching "${query}"</p>
              </div>
              <div class="d-flex justify-content-evenly flex-md-row flex-column">
                <div class="btn btn-primary mx-auto mx-md-0 btn-livechat my-2">Live Chat <i class="bi bi-person-workspace ms-1"></i></div>
                <a class="my-2" href="tel:01233713581">
                  <div class="btn btn-primary">Call us now<i class="bi bi-telephone-fill ms-1"></i></div>
                </a>
                <a class="my-2" href="/search-results-110-w.asp?q=${query}">
                  <div class="btn btn-danger text-white">View All Results<i class="bi bi-box-arrow-in-right"></i></div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    `;

    return $(template);
  }

  highlightText(text, query) {
    if (!query) return text;
    try {
      const regex = new RegExp(`(${query.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')})`, 'gi');

      return text.replace(regex, '<mark class="bg-warning">$1</mark>');
    } catch (e) {
      return text;
    }
  }
}

// Initialize the search when the DOM is ready
$(document).ready(() => {
  new AlgoliaAutocompleteCore();
});

// Insights
aa('init', {
  appId: process.env.ALGOLIA_APP_ID,
  apiKey: process.env.ALGOLIA_SEARCH_KEY,
  useCookie: true,
});

// Send Click Event
$(document).on('click', '.search-item', function () {
  const id = $(this).data('product-id').toString();

  if (id && window.queryID) {
    aa('clickedObjectIDsAfterSearch', {
      eventName: 'Product Clicked',
      index: 'prestashop',
      objectIDs: [id],
      positions: [1],
      queryID: window.queryID,
    });
  }
});
